import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Botao, Cartao } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Entrada, Selecao } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, ErroRequisicao, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { rotuloEtapaPipeline, rotuloFunilPipeline } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';

interface Item {
  id: string;
  nome: string;
}

/** Aceita lista pura ou `{ itens }` e lê o nome em `nome`, `rotulo` ou `titulo`. */
function normalizar(resposta: unknown): Item[] {
  const lista = Array.isArray(resposta) ? resposta : ((resposta as { itens?: unknown } | null)?.itens ?? []);
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object' && 'id' in x)
    .map((x) => ({ id: String(x.id), nome: String(x.nome ?? x.rotulo ?? x.titulo ?? x.id) }));
}

interface Definicao {
  /** Módulo dono: a seção só aparece se ele estiver instalado. */
  modulo: string;
  chave: 'etapas' | 'funcoes';
  /** Rota HTTP do módulo dono, sem importar código dele. */
  caminho: string;
  /** Campo `tipo` obrigatório na criação (lista com tipo, como receita ou despesa). */
  tipos?: string[];
  /**
   * Review r17 spec (B4): a lista é editada só na tela do módulo dono (rota do web) e o cartão daqui fica só de consulta, com
   * o link para lá. Uma permissão de ver o módulo dono mostra o link.
   */
  editarEm?: { rota: string; permissao: string };
}

/**
 * Fase 2 (W5, L4): Categorias saíram daqui. Eram editáveis em dois lugares (aqui e em Financeiro > Categorias, a mesma
 * rota): a aba do Financeiro, dona do dado, é o lugar único. Review r17 spec (B4): pelo mesmo motivo, as Funções são
 * editadas só em Pessoas > Funções; aqui a lista fica de consulta, com o link para lá.
 */
export const SECOES: Definicao[] = [
  { modulo: 'pipeline', chave: 'etapas', caminho: '/pipeline/etapas' },
  { modulo: 'pessoas', chave: 'funcoes', caminho: '/pessoas/funcoes', editarEm: { rota: '/pessoas/pessoas/funcoes', permissao: 'pessoas.ver' } },
];

function Secao({ def }: { def: Definicao }) {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = s.pode('configuracoes.editar') && !def.editarEm;
  // U13: as etapas são de um funil; a lista começa no padrão e a escolha muda a lista e onde a etapa nova entra.
  const [funil, setFunil] = useState('');
  const funis = useQuery({
    queryKey: ['configuracoes', 'estruturas', 'funis'],
    queryFn: () => get<{ itens: { id: string; nome: string; padrao: boolean }[] }>('/pipeline/funis'),
    enabled: def.chave === 'etapas',
    retry: false,
  });
  // O cache da lista é o mesmo nos dois cartões: só o de etapas usa (o de funções não tem funil).
  const opcoesFunil = def.chave === 'etapas' ? (funis.data?.itens ?? []) : [];
  const funilId = def.chave === 'etapas' ? (opcoesFunil.find((f) => f.id === funil)?.id ?? opcoesFunil.find((f) => f.padrao)?.id ?? '') : '';
  const comFunil = funilId ? { funil_id: funilId } : {};
  const chave = ['configuracoes', 'estruturas', def.caminho, funilId];
  const exibir = (nome: string) => (def.chave === 'etapas' ? rotuloEtapaPipeline(nome) : nome);
  const [novo, setNovo] = useState('');
  const [tipo, setTipo] = useState(def.tipos?.[0] ?? '');
  const [editando, setEditando] = useState<{ id: string; nome: string } | null>(null);
  const [removendo, setRemovendo] = useState<Item | null>(null);

  const q = useQuery({
    queryKey: chave,
    queryFn: async () => normalizar(await get<unknown>(def.caminho, comFunil)),
    retry: false,
  });
  const indisponivel = q.error instanceof ErroRequisicao && (q.error.status === 404 || q.error.status === 405);
  const recarregar = () => qc.invalidateQueries({ queryKey: chave });
  const falha = (e: unknown) => toast.erro(mensagemDeErro(e));

  const criar = useMutation({
    mutationFn: () => post(def.caminho, { nome: novo.trim(), ...(def.tipos ? { tipo } : {}), ...comFunil }),
    onSuccess: async () => {
      setNovo('');
      await recarregar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: falha,
  });
  const renomear = useMutation({
    mutationFn: (v: { id: string; nome: string }) => patch(`${def.caminho}/${v.id}`, { nome: v.nome.trim() }),
    onSuccess: async () => {
      setEditando(null);
      await recarregar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: falha,
  });
  const remover = useMutation({
    mutationFn: (id: string) => del(`${def.caminho}/${id}`),
    onSuccess: async () => {
      setRemovendo(null);
      await recarregar();
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => {
      setRemovendo(null);
      falha(e);
    },
  });

  return (
    <Cartao
      titulo={t(`estruturas.${def.chave}`)}
      acao={
        def.editarEm && s.pode(def.editarEm.permissao) ? (
          <Link to={def.editarEm.rota} className="text-sm font-medium text-acento-forte underline">
            {t(`estruturas.editarEm.${def.chave}`)}
          </Link>
        ) : undefined
      }
      semPadding
    >
      {q.isPending ? (
        <Carregando />
      ) : indisponivel ? (
        <Vazio texto={t('estruturas.indisponivel')} />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : (
        <>
          {opcoesFunil.length > 1 && (
            <div className="border-b border-borda p-3">
              <Selecao aria-label={t('estruturas.funil')} opcoes={opcoesFunil.map((f) => ({ valor: f.id, rotulo: rotuloFunilPipeline(f.nome) }))} value={funilId} onChange={(e) => e.target.value && setFunil(e.target.value)} />
            </div>
          )}
          {podeEditar && (
            <form
              className="flex flex-wrap gap-2 border-b border-borda p-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (novo.trim()) criar.mutate();
              }}
            >
              <Entrada aria-label={t('estruturas.nomeNovo')} placeholder={t('estruturas.nomeNovo')} value={novo} onChange={(e) => setNovo(e.target.value)} maxLength={80} className="min-w-0 flex-1 basis-40" />
              {def.tipos && (
                <div className="w-36">
                  <Selecao aria-label={t('estruturas.tipo')} opcoes={def.tipos.map((v) => ({ valor: v, rotulo: t(`estruturas.tipos.${v}`) }))} value={tipo} onChange={(e) => setTipo(e.target.value)} />
                </div>
              )}
              <Botao type="submit" variante="secundario" icone={<Plus className="size-4" />} carregando={criar.isPending}>
                {t('acoes.adicionar')}
              </Botao>
            </form>
          )}
          {q.data.length === 0 ? (
            <Vazio texto={t('estruturas.vazio')} />
          ) : (
            <ul className="divide-y divide-borda">
              {q.data.map((i) => (
                <li key={i.id} className="flex min-w-0 items-center gap-2 px-4 py-2.5 text-sm">
                  {editando?.id === i.id ? (
                    <form
                      className="flex min-w-0 flex-1 items-center gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (editando.nome.trim()) renomear.mutate(editando);
                      }}
                    >
                      <Entrada aria-label={t('acoes.renomear')} autoFocus value={editando.nome} onChange={(e) => setEditando({ id: i.id, nome: e.target.value })} maxLength={80} className="h-9 min-w-0 flex-1" />
                      <button type="submit" aria-label={tn('comum.salvar')} className="rounded p-1.5 text-verde hover:bg-sup-2">
                        <Check className="size-4" />
                      </button>
                      <button type="button" aria-label={tn('comum.cancelar')} onClick={() => setEditando(null)} className="rounded p-1.5 text-tinta-3 hover:bg-sup-2">
                        <X className="size-4" />
                      </button>
                    </form>
                  ) : (
                    <>
                      {/* Etapa de fábrica do pipeline aparece no idioma do app (r7 visual B6); renomear edita o nome gravado. */}
                      <span className="min-w-0 flex-1 truncate" title={exibir(i.nome)}>{exibir(i.nome)}</span>
                      {podeEditar && (
                        <>
                          <button type="button" aria-label={`${t('acoes.renomear')}: ${exibir(i.nome)}`} onClick={() => setEditando({ id: i.id, nome: i.nome })} className="rounded p-1.5 text-tinta-3 hover:text-tinta-1">
                            <Pencil className="size-4" />
                          </button>
                          <button type="button" aria-label={`${t('acoes.remover')}: ${exibir(i.nome)}`} onClick={() => setRemovendo(i)} className="rounded p-1.5 text-tinta-3 hover:text-perigo">
                            <Trash2 className="size-4" />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <Confirmar
        aberto={!!removendo}
        aoFechar={() => setRemovendo(null)}
        aoConfirmar={() => removendo && remover.mutate(removendo.id)}
        carregando={remover.isPending}
        perigoso
        titulo={t('estruturas.confirmarRemover')}
        texto={t('estruturas.confirmarRemoverTexto')}
      />
    </Cartao>
  );
}

/** M13.5: etapas do pipeline e funções, editadas pelas rotas HTTP dos módulos donos (categorias ficam no Financeiro, fase 2). */
export function Estruturas() {
  const { t } = useTranslation('configuracoes');
  const s = useSessao();
  const visiveis = SECOES.filter((x) => s.instalado(x.modulo));
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="text-sm text-tinta-3">{t('estruturas.ajuda')}</p>
      {visiveis.length === 0 ? (
        <Cartao>
          <Vazio texto={t('estruturas.semModulos')} />
        </Cartao>
      ) : (
        <div className="grid min-w-0 gap-4 xl:grid-cols-2">
          {visiveis.map((d) => (
            <Secao key={d.chave} def={d} />
          ))}
        </div>
      )}
    </div>
  );
}
